import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { EnecoLogo, VisuallyHidden } from '@eneco/sparky';

import type { LogoContent } from './logo-content.types';

/**
 * `Logo` block for the CMS-agnostic lane, serving **both** providers.
 *
 * The logo mark is a hardcoded brand SVG, not CMS content — matching production exactly
 * (`libs/sitecore/components/eneco/src/ImageLink/ImageLink.tsx`, registered as the `Logo` rendering in
 * every Eneco-branded `sitecoreComponents.ts`): it too renders `<EnecoLogo />` unconditionally and reads
 * the CMS only for the click-through `link` and its accessible text. `siteWideImageLink.image` — the
 * field this block used to (wrongly) treat as the logo's source — is unused by production and, on real
 * content, has no asset behind it either; neither is a bug to chase.
 *
 * Hardcodes `EnecoLogo` rather than selecting a brand at render time: this lane is wired up for
 * Eneco-branded containers only today (`gb-eneco-main`, `nl/eneco/main`, `nl/eneco/mkb`), the same
 * scope `ImageLink.tsx` has per brand package (`libs/sitecore/components/{eneco,oxxio,woonenergie}`).
 * A second brand joining the lane needs the same per-brand split, not a runtime switch here.
 */
const Logo: FC = () => {
  const content = useContent<LogoContent>();
  const link = linkWithUrl(content.siteWideContent?.link);

  const logo = (
    <>
      <VisuallyHidden>{link?.text ?? 'Eneco'}</VisuallyHidden>
      <EnecoLogo />
    </>
  );

  if (!link) return logo;

  return (
    // Sparky's own link components (TextLink/NavLink) default rel to 'noreferrer' whenever
    // target is '_blank', guarding against reverse tabnabbing; this is a bare <a>, so the same
    // default is applied explicitly. `link.rel` (set with target: '_blank' when a link is converted) wins
    // when present; the fallback only matters for a link shape that sets target without it.
    <a href={link.href} target={link.target} rel={link.rel ?? (link.target === '_blank' ? 'noreferrer' : undefined)}>
      {logo}
    </a>
  );
};

export default Logo;
